/**
 * Selector de navegación del LECTOR: libro → capítulo → versículo.
 *
 * Componente autocontenido (parte de la feature `reader`). Renderiza un diálogo
 * con tres pestañas (Book / Chapter / Verse). Al elegir destino llama a
 * `onNavigate({ bookId, abbr, chapter, verse? })`; el contenedor decide la ruta.
 *
 * URL-addressability (#17): el selector es **controlado por la URL**. Cada paso
 * del drill-down vive en `?nav=` (`book`, `book:gen`, `book:gen:4`), así que el
 * popup abierto, el paso de capítulo-de-libro y el paso de versículo-de-capítulo
 * tienen CADA UNO su propia URL copiable que reabre exactamente esa pantalla en
 * otro navegador. El componente no guarda el paso en signals locales: lo lee de
 * `props.step` y los avances de paso llaman a `onStepChange` (el contenedor
 * escribe a la URL con `replace`).
 *
 * El conteo de versículos del capítulo elegido se obtiene OFFLINE de la base
 * local (bible_verse_content). Si el capítulo no está sembrado localmente, se
 * cae a un grid genérico basado en un máximo razonable para que el selector
 * siga siendo usable sin red.
 */

import { createResource, For, Show } from "solid-js";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";
import { Dialog } from "@/components/ui/dialog";
import { bibleBooks, bookChapters } from "@/utils/bible-books-data";
import type { PowerSyncDrizzleDB } from "@bso/powersync";
import { loadChapterFromLocal } from "../queries/load-chapter";

const LAST_OT_BOOK_ID = 39;
const FALLBACK_VERSE_COUNT = 40;

export interface ReaderNavTarget {
  bookId: number;
  abbr: string;
  chapter: number;
  verse?: number;
}

/** Paso activo del selector, gobernado por la URL (`?nav=`) del contenedor. */
export interface ReaderNavStep {
  tab: "book" | "chapter" | "verse";
  bookAbbr?: string;
  chapter?: number;
}

interface ReaderNavSelectorProps {
  open: boolean;
  /** Paso activo (fuente de verdad en la URL). `undefined` = popup cerrado. */
  step: ReaderNavStep | undefined;
  /** Avanza/retrocede de paso escribiendo el nuevo paso a la URL. */
  onStepChange: (step: ReaderNavStep) => void;
  /** Cierra el popup (limpia `?nav=`). */
  onClose: () => void;
  /** Versión actual (abbr en minúsculas, p. ej. "kjv"). */
  version: string;
  /** Libro actualmente abierto (para resaltar el activo). */
  currentBookAbbr: string;
  /** Capítulo actualmente abierto (para resaltar el activo). */
  currentChapter: number;
  /** Cliente drizzle/PowerSync para el conteo de versículos offline. */
  db: PowerSyncDrizzleDB | null;
  onNavigate: (target: ReaderNavTarget) => void;
}

interface SelectedBook {
  id: number;
  name: MessageDescriptor;
  abbr: string;
}

export function ReaderNavSelector(props: ReaderNavSelectorProps) {
  const { t } = useI18n();

  // Estado derivado de la URL (props.step) — NO de signals locales.
  const activeTab = (): "book" | "chapter" | "verse" => props.step?.tab ?? "book";
  const selectedBook = (): SelectedBook | null => {
    const abbr = props.step?.bookAbbr;
    if (!abbr) {
      return null;
    }
    const book = bibleBooks.find((b) => b.abbr === abbr);
    return book ? { id: book.id, name: book.name, abbr: book.abbr } : null;
  };
  const pendingChapter = (): number | null => props.step?.chapter ?? null;

  // Conteo de versículos del capítulo pendiente, desde la base local.
  const [verseCount] = createResource(
    () => {
      const b = selectedBook();
      const ch = pendingChapter();
      return b && ch ? { book: b.id, chapter: ch } : null;
    },
    // El fetcher de createResource corre dentro del scope rastreado por su
    // source (arriba); leer props.db/props.version aquí no es reactividad
    // perdida. Falso positivo de solid/reactivity.
    // eslint-disable-next-line solid/reactivity
    async (q): Promise<number> => {
      const verses = await loadChapterFromLocal(props.db, props.version, q.book, q.chapter);
      return verses.length > 0 ? verses.length : FALLBACK_VERSE_COUNT;
    },
  );

  function openBook(book: SelectedBook) {
    // Paso de capítulo-de-libro → su propia URL (`?nav=book:<abbr>`).
    props.onStepChange({ tab: "chapter", bookAbbr: book.abbr });
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) {
          props.onClose();
        }
      }}
    >
      <div
        class="p-4 md:p-6 max-h-[80vh] overflow-y-auto flex flex-col select-none"
        data-testid="reader-nav"
      >
        <h2 class="text-lg font-bold text-foreground mb-4 shrink-0">
          {t(msg`Biblical Navigation`)}
        </h2>

        {/* Tabs */}
        <div class="flex bg-muted/40 p-1 rounded-xl mb-4 shrink-0">
          <button
            type="button"
            onClick={() => props.onStepChange({ tab: "book" })}
            class="flex-1 py-2 text-xs font-bold rounded-lg text-center transition-all duration-150"
            classList={{
              "bg-card text-foreground shadow-sm": activeTab() === "book",
              "text-muted-foreground hover:text-foreground": activeTab() !== "book",
            }}
          >
            {t(msg`Book`)}
          </button>
          <button
            type="button"
            onClick={() => {
              const b = selectedBook();
              if (b) {
                props.onStepChange({ tab: "chapter", bookAbbr: b.abbr });
              }
            }}
            disabled={!selectedBook()}
            class="flex-1 py-2 text-xs font-bold rounded-lg text-center transition-all duration-150 disabled:opacity-40"
            classList={{
              "bg-card text-foreground shadow-sm": activeTab() === "chapter",
              "text-muted-foreground hover:text-foreground": activeTab() !== "chapter",
            }}
          >
            {t(msg`Chapter`)}
          </button>
          <button
            type="button"
            onClick={() => {
              const b = selectedBook();
              const ch = pendingChapter();
              if (b && ch) {
                props.onStepChange({ tab: "verse", bookAbbr: b.abbr, chapter: ch });
              }
            }}
            disabled={!pendingChapter()}
            class="flex-1 py-2 text-xs font-bold rounded-lg text-center transition-all duration-150 disabled:opacity-40"
            classList={{
              "bg-card text-foreground shadow-sm": activeTab() === "verse",
              "text-muted-foreground hover:text-foreground": activeTab() !== "verse",
            }}
          >
            {t(msg`Verse`)}
          </button>
        </div>

        {/* Books */}
        <Show when={activeTab() === "book"}>
          <div class="space-y-4 flex-1 overflow-y-auto pr-1">
            <div>
              <h3 class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 mb-2">
                {t(msg`Old Testament`)}
              </h3>
              <div class="grid grid-cols-3 gap-2">
                <For each={bibleBooks.filter((b) => b.id <= LAST_OT_BOOK_ID)}>
                  {(book) => (
                    <button
                      type="button"
                      onClick={() => openBook(book)}
                      class="px-2 py-2.5 text-xs font-semibold rounded-xl bg-card border border-border/30 hover:border-primary/50 hover:text-primary text-center transition-all truncate"
                      classList={{
                        "bg-primary/10 border-primary text-primary font-bold":
                          props.currentBookAbbr === book.abbr,
                      }}
                    >
                      {t(book.name)}
                    </button>
                  )}
                </For>
              </div>
            </div>

            <div>
              <h3 class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/60 mb-2">
                {t(msg`New Testament`)}
              </h3>
              <div class="grid grid-cols-3 gap-2">
                <For each={bibleBooks.filter((b) => b.id > LAST_OT_BOOK_ID)}>
                  {(book) => (
                    <button
                      type="button"
                      onClick={() => openBook(book)}
                      class="px-2 py-2.5 text-xs font-semibold rounded-xl bg-card border border-border/30 hover:border-primary/50 hover:text-primary text-center transition-all truncate"
                      classList={{
                        "bg-primary/10 border-primary text-primary font-bold":
                          props.currentBookAbbr === book.abbr,
                      }}
                    >
                      {t(book.name)}
                    </button>
                  )}
                </For>
              </div>
            </div>
          </div>
        </Show>

        {/* Chapters */}
        <Show when={activeTab() === "chapter" && selectedBook()}>
          {(b) => (
            <div class="flex flex-col flex-1 overflow-y-auto">
              <div class="flex items-center gap-2 mb-4 shrink-0">
                <button
                  type="button"
                  onClick={() => props.onStepChange({ tab: "book" })}
                  class="text-xs font-semibold text-primary hover:underline"
                >
                  ← {t(msg`Books`)}
                </button>
                <span class="text-xs text-muted-foreground/60">|</span>
                <span class="text-sm font-bold text-foreground">{t(b().name)}</span>
              </div>
              <div class="grid grid-cols-5 gap-2 pr-1">
                <For each={Array.from({ length: bookChapters[b().id] ?? 1 }, (_, i) => i + 1)}>
                  {(ch) => (
                    <button
                      type="button"
                      onClick={() => {
                        // Paso de versículo-de-capítulo → su propia URL.
                        props.onStepChange({ tab: "verse", bookAbbr: b().abbr, chapter: ch });
                      }}
                      class="h-10 flex items-center justify-center text-xs font-bold rounded-xl bg-card border border-border/30 hover:border-primary hover:text-primary transition-all"
                      classList={{
                        "bg-primary/10 border-primary text-primary font-bold":
                          props.currentBookAbbr === b().abbr && props.currentChapter === ch,
                      }}
                    >
                      {ch}
                    </button>
                  )}
                </For>
              </div>
            </div>
          )}
        </Show>

        {/* Verses */}
        <Show when={activeTab() === "verse" && selectedBook() && pendingChapter()}>
          <div class="flex flex-col flex-1 overflow-y-auto">
            <div class="flex items-center gap-2 mb-3 shrink-0">
              <button
                type="button"
                onClick={() => {
                  const b = selectedBook();
                  if (b) {
                    props.onStepChange({ tab: "chapter", bookAbbr: b.abbr });
                  }
                }}
                class="text-xs font-semibold text-primary hover:underline"
              >
                ← {t(msg`Chapters`)}
              </button>
              <span class="text-xs text-muted-foreground/60">|</span>
              <span class="text-sm font-bold text-foreground">
                {(() => {
                  const b = selectedBook();
                  return b ? t(b.name) : "";
                })()}{" "}
                {pendingChapter()}
              </span>
            </div>

            <button
              type="button"
              onClick={() => {
                const b = selectedBook();
                const ch = pendingChapter();
                if (!b || !ch) {
                  return;
                }
                // Navegar al destino limpia `?nav=` (cierra el selector); no
                // cerramos por separado para no pisar la navegación (Patrón A).
                props.onNavigate({ bookId: b.id, abbr: b.abbr, chapter: ch });
              }}
              class="mb-3 w-full py-2.5 bg-primary text-primary-foreground hover:bg-primary/95 text-xs font-bold rounded-xl transition-all shadow-sm active:scale-98 shrink-0"
            >
              {t(msg`Read full chapter`)}
            </button>

            <div class="grid grid-cols-6 gap-2 pr-1">
              <For
                each={Array.from({ length: verseCount() ?? FALLBACK_VERSE_COUNT }, (_, i) => i + 1)}
              >
                {(v) => (
                  <button
                    type="button"
                    onClick={() => {
                      const b = selectedBook();
                      const ch = pendingChapter();
                      if (!b || !ch) {
                        return;
                      }
                      props.onNavigate({ bookId: b.id, abbr: b.abbr, chapter: ch, verse: v });
                    }}
                    class="h-9 flex items-center justify-center text-xs font-bold rounded-xl bg-card border border-border/30 hover:border-primary hover:text-primary transition-all"
                  >
                    {v}
                  </button>
                )}
              </For>
            </div>
          </div>
        </Show>
      </div>
    </Dialog>
  );
}
